import { faPlus, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    CellContent,
    CompactButton,
    CompactPrimaryCell,
    CompactPrimaryCellContent,
    CompactSecondaryCell,
    CompactSecondaryTable,
} from 'components/data_table/styled';
import { BaseTableRowProps, TableColumn } from 'components/data_table/types';
import { useToggle } from 'custom_hooks/useToggle';

export type CompactTableRowProps<TData> = BaseTableRowProps<TData> &
    Readonly<{
        /**
         * Columns that will always be visible.
         */
        primaryColumns: TableColumn<TData>[];
        /**
         * Columns that will only be visible when the row is expanded.
         */
        secondaryColumns: TableColumn<TData>[];
        /**
         * The description of the row used for the expand/collapse button.
         */
        ariaDescription: string;
    }>;

export function CompactTableRow<TData>({
    primaryColumns,
    secondaryColumns,
    tableStyle = 'data',
    ariaDescription,
    valueCell,
}: CompactTableRowProps<TData>) {
    const [expanded, toggleExpanded] = useToggle();

    return (
        <>
            <tr>
                {primaryColumns.map((columnInfo) => (
                    <CompactPrimaryCell
                        className={columnInfo.valueClassName}
                        key={columnInfo.id}
                    >
                        <CompactPrimaryCellContent $tableStyle={tableStyle}>
                            {valueCell(columnInfo)}
                        </CompactPrimaryCellContent>
                    </CompactPrimaryCell>
                ))}
                <CompactPrimaryCell>
                    <div className="flex-row justify-flex-end">
                        <CompactButton
                            onClick={toggleExpanded}
                            aria-label={`${
                                expanded ? 'collapse' : 'expand'
                            } ${ariaDescription}`}
                        >
                            <FontAwesomeIcon
                                className="f-11"
                                icon={expanded ? faTimes : faPlus}
                            />
                        </CompactButton>
                    </div>
                </CompactPrimaryCell>
            </tr>
            {expanded && (
                <tr>
                    <CompactSecondaryCell colSpan={primaryColumns.length + 1}>
                        <CompactSecondaryTable $tableStyle={tableStyle}>
                            <tbody>
                                {secondaryColumns.map((columnInfo) => (
                                    <tr key={columnInfo.id}>
                                        <td>
                                            <CellContent>
                                                {columnInfo.header({
                                                    columnInfo,
                                                    isCompact: true,
                                                })}
                                            </CellContent>
                                        </td>
                                        <td className={columnInfo.valueClassName}>
                                            <CellContent>
                                                {valueCell(columnInfo)}
                                            </CellContent>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </CompactSecondaryTable>
                    </CompactSecondaryCell>
                </tr>
            )}
        </>
    );
}
